import { assert } from '@ftr/contracts/shared/Assertions'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { LocalTimeRange } from '@ftr/contracts/type/shared/LocalTimeRange'
import { SttEdit } from '@ftr/regional-api-contracts'
import { RealTimeSttSegmentMap } from '../../../types'
import { ValidationErrorResult } from './operation-result'

export const SessionBoundaryErrors = [
  'start-end-different-sessions',
  'start-end-not-in-session',
  'start-not-in-session',
  'end-not-in-session',
] as const
export const SealedBoundaryErrors = [
  'start-end-span-inaccessible-sealed',
  'start-end-in-inaccessible-sealed',
  'start-in-inaccessible-sealed',
  'end-in-inaccessible-sealed',
] as const

type SessionBoundaryError = (typeof SessionBoundaryErrors)[number]
type SealedBoundaryError = (typeof SealedBoundaryErrors)[number]
export type BoundaryError = SessionBoundaryError | SealedBoundaryError

export function checkEditForBoundaryErrors(
  edit: SttEdit,
  segmentMap: RealTimeSttSegmentMap,
): BoundaryError | undefined {
  switch (edit.type) {
    case 'deleteUtterance':
      return undefined
    case 'softCreateUtterance':
      return checkTimeRangeForBoundaryErrors(
        new LocalTimeRange(edit.startTime.toLocalTime(), edit.endTime.toLocalTime()),
        segmentMap,
      )
    case 'upsertUtterance': {
      const { fragments } = edit
      assert(fragments.length)
      return checkTimeRangeForBoundaryErrors(
        new LocalTimeRange(fragments[0].startTime.toLocalTime(), fragments[fragments.length - 1].endTime.toLocalTime()),
        segmentMap,
      )
    }
    default:
      assertUnreachable(edit)
  }
}

export function boundaryErrorToValidationResult(error: BoundaryError): ValidationErrorResult {
  switch (error) {
    case 'start-end-different-sessions':
      return {
        type: 'validationError',
        message: 'Start and end times are within different sessions.',
        invalidFields: ['startTime', 'endTime'],
      }
    case 'start-end-not-in-session':
      return {
        type: 'validationError',
        message: 'Start and end times are not within a session.',
        invalidFields: ['startTime', 'endTime'],
      }
    case 'start-not-in-session':
      return {
        type: 'validationError',
        message: 'Start time is not within a session.',
        invalidFields: ['startTime'],
      }
    case 'end-not-in-session':
      return {
        type: 'validationError',
        message: 'End time is not within a session.',
        invalidFields: ['endTime'],
      }
    case 'start-end-span-inaccessible-sealed':
      return {
        type: 'validationError',
        message: "Start and end times span restricted content you don't have permission to access.",
        invalidFields: ['startTime', 'endTime'],
      }
    case 'start-end-in-inaccessible-sealed':
      return {
        type: 'validationError',
        message: "Start and end times fall within restricted content you don't have permission to access.",
        invalidFields: ['startTime', 'endTime'],
      }
    case 'start-in-inaccessible-sealed':
      return {
        type: 'validationError',
        message: "Start time falls within restricted content you don't have permission to access.",
        invalidFields: ['startTime'],
      }
    case 'end-in-inaccessible-sealed':
      return {
        type: 'validationError',
        message: "End time falls within restricted content you don't have permission to access.",
        invalidFields: ['endTime'],
      }
    default: {
      assertUnreachable(error)
    }
  }
}

function checkTimeRangeForBoundaryErrors(
  timeRange: LocalTimeRange,
  segmentMap: RealTimeSttSegmentMap,
): BoundaryError | undefined {
  // Check sessions
  const sessionTimeRanges = segmentMap.sessionMarkers.flatMap(marker => marker.sessionTimes)
  const entirelyInASession = sessionTimeRanges.some(sessionTimeRange => sessionTimeRange.encloses(timeRange))
  if (!entirelyInASession) {
    const startInASession = sessionTimeRanges.some(sessionTimeRange => sessionTimeRange.contains(timeRange.start))
    const endInASession = sessionTimeRanges.some(
      sessionTimeRange =>
        sessionTimeRange.contains(timeRange.end) ||
        // Contains is not end inclusive
        sessionTimeRange.end.equals(timeRange.end),
    )
    if (startInASession && endInASession) {
      return 'start-end-different-sessions'
    }

    if (!startInASession && !endInASession) {
      return 'start-end-not-in-session'
    }

    return startInASession ? 'end-not-in-session' : 'start-not-in-session'
  }

  // Check sealed blocks
  const inaccessibleSealedTimeRanges = segmentMap.sealingMarkers
    .filter(sealed => !sealed.canAccessSealedContent)
    .flatMap(marker => marker.sealedTimeRange)
  const timesSpanInaccessibleSealedRange = inaccessibleSealedTimeRanges.some(sealedTimeRange =>
    timeRange.encloses(sealedTimeRange),
  )
  if (timesSpanInaccessibleSealedRange) {
    return 'start-end-span-inaccessible-sealed'
  }
  const startInaccessibleSealedRange = inaccessibleSealedTimeRanges.some(sealedTimeRange =>
    sealedTimeRange.contains(timeRange.start),
  )
  const endInaccessibleSealedRange = inaccessibleSealedTimeRanges.some(sealedTimeRange =>
    sealedTimeRange.contains(timeRange.end),
  )
  if (startInaccessibleSealedRange && endInaccessibleSealedRange) {
    return 'start-end-in-inaccessible-sealed'
  }
  if (startInaccessibleSealedRange) {
    return 'start-in-inaccessible-sealed'
  }
  if (endInaccessibleSealedRange) {
    return 'end-in-inaccessible-sealed'
  }

  return undefined
}
